package com.hyperether.planner.util

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch

@Composable
fun CircleWithInitial(
    name: String?,
    size: androidx.compose.ui.unit.Dp = Dp(32f),
    modifier: Modifier = Modifier,
    fontSize: Int = 18
) {
    val firstLetter = name?.firstOrNull()?.uppercaseChar()?.toString() ?: ""

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier
            .size(size)
            .clip(CircleShape) // Clip the background into a circle
            .background(Color(0xFF34618E))
    ) {
        Text(
            text = firstLetter,
            color = Color.White, // Text color
            fontSize = Sp(fontSize.toFloat()),
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun Modifier.verticalScrollbar(
    state: ScrollState,
    scrollbarWidth: androidx.compose.ui.unit.Dp = 12.dp,
    color: Color = Color.LightGray.copy(alpha = 1f)
): Modifier {
//    val alpha by animateFloatAsState(targetValue = if(state.isScrollInProgress) 1f else 0f,
//        animationSpec = tween(400, delayMillis = if(state.isScrollInProgress) 0 else 700)
//    )
    val coroutineScope = rememberCoroutineScope()
    return this then Modifier
        .pointerInput(state) {
            detectDragGestures(
                onDragStart = {},
                onDrag = { change, offset ->
                    val viewHeight = state.viewportSize.toFloat()
                    val contentHeight = state.maxValue + viewHeight

                    if (contentHeight > viewHeight) {
                        val scrollbarHeight = try {
                            (viewHeight * (viewHeight / contentHeight))
                                .coerceIn(10.dp.toPx()..viewHeight)
                        } catch (e: Exception) {
                            0f
                        }
                        val variableZone = viewHeight - scrollbarHeight

                        // Convert drag delta to scroll delta
                        val dragRatio = offset.y / variableZone
                        val scrollDelta = dragRatio * state.maxValue

                        coroutineScope.launch {
                            state.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()


            val viewHeight = state.viewportSize.toFloat()
            val contentHeight = state.maxValue + viewHeight

            val scrollbarHeight = try {
                (viewHeight * (viewHeight / contentHeight)).coerceIn(10.dp.toPx()..viewHeight)
            } catch (e: Exception) {
                0f
            }
            val variableZone = viewHeight - scrollbarHeight
            val scrollbarYoffset = (state.value.toFloat() / state.maxValue) * variableZone

            drawRoundRect(
                cornerRadius = CornerRadius(scrollbarWidth.toPx() / 2, scrollbarWidth.toPx() / 2),
                color = color,
                topLeft = Offset(this.size.width - scrollbarWidth.toPx(), scrollbarYoffset),
                size = Size(scrollbarWidth.toPx(), scrollbarHeight)
            )
        }
}

@Composable
fun Modifier.verticalScrollbarGrid(
    state: LazyGridState,
    columns: Int,
    scrollbarWidth: Dp = 12.dp,
    color: Color = Color.LightGray.copy(alpha = 1f)
): Modifier {
    val coroutineScope = rememberCoroutineScope()

    return this
        .pointerInput(state) {
            detectDragGestures { _, dragAmount ->
                val layoutInfo = state.layoutInfo
                val viewportHeight = layoutInfo.viewportEndOffset - layoutInfo.viewportStartOffset
                val itemHeight = layoutInfo.visibleItemsInfo.firstOrNull()?.size?.height ?: return@detectDragGestures

                val totalRows = (layoutInfo.totalItemsCount + columns - 1) / columns
                val totalContentHeight = totalRows * itemHeight

                if (totalContentHeight <= viewportHeight) return@detectDragGestures

                val scrollbarHeight =
                    (viewportHeight.toFloat() * viewportHeight / totalContentHeight)
                        .coerceIn(10.dp.toPx()..viewportHeight.toFloat())

                val variableZone = viewportHeight - scrollbarHeight

                val contentPerPixel = (totalContentHeight - viewportHeight) / variableZone

                val scrollDelta = dragAmount.y * contentPerPixel

                coroutineScope.launch {
                    state.scrollBy(scrollDelta)
                }
            }
        }
        .drawWithContent {
            drawContent()

            val layoutInfo = state.layoutInfo
            if (layoutInfo.totalItemsCount == 0) return@drawWithContent

            val viewportHeight = layoutInfo.viewportEndOffset - layoutInfo.viewportStartOffset
            val itemHeight = layoutInfo.visibleItemsInfo.firstOrNull()?.size?.height ?: return@drawWithContent

            val totalRows = (layoutInfo.totalItemsCount + columns - 1) / columns
            val totalContentHeight = totalRows * itemHeight

            if (totalContentHeight <= viewportHeight) return@drawWithContent

            val scrollbarHeight =
                (viewportHeight.toFloat() * viewportHeight / totalContentHeight)
                    .coerceIn(10.dp.toPx()..viewportHeight.toFloat())

            val variableZone = viewportHeight - scrollbarHeight

            val scrolledRows = state.firstVisibleItemIndex / columns
            val scrollOffsetPx = scrolledRows * itemHeight + state.firstVisibleItemScrollOffset

            val scrollY = (scrollOffsetPx / (totalContentHeight - viewportHeight)) * variableZone

            drawRoundRect(
                color = color,
                topLeft = Offset(size.width - scrollbarWidth.toPx(), scrollY),
                size = Size(scrollbarWidth.toPx(), scrollbarHeight),
                cornerRadius = CornerRadius(scrollbarWidth.toPx() / 2)
            )
        }
}

@Composable
fun Modifier.horizontalScrollbar(
    state: ScrollState,
    scrollbarHeight: Dp = 12.dp,
    color: Color = Color.LightGray.copy(alpha = 1f)
): Modifier {
    val coroutineScope = rememberCoroutineScope()

    return this then Modifier
        .pointerInput(state) {
            detectDragGestures(
                onDragStart = {},
                onDrag = { change, offset ->
                    val viewWidth = state.viewportSize.toFloat()
                    val contentWidth = state.maxValue + viewWidth

                    if (contentWidth > viewWidth) {
                        val scrollbarWidth = try {
                            (viewWidth * (viewWidth / contentWidth))
                                .coerceIn(10.dp.toPx()..viewWidth)
                        } catch (e: Exception) {
                            0f
                        }
                        val variableZone = viewWidth - scrollbarWidth

                        // Convert drag delta to scroll delta (negated to fix direction)
                        val dragRatio = -offset.x / variableZone  // Note the negative sign
                        val scrollDelta = dragRatio * state.maxValue

                        coroutineScope.launch {
                            state.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()

            val viewWidth = state.viewportSize.toFloat()
            val contentWidth = state.maxValue + viewWidth

            val scrollbarWidth = try {
                (viewWidth * (viewWidth / contentWidth))
                    .coerceIn(10.dp.toPx()..viewWidth)
            } catch (e: Exception) {
                0f
            }
            val variableZone = viewWidth - scrollbarWidth
            val scrollbarXoffset = if (state.maxValue > 0) {
                (state.value.toFloat() / state.maxValue) * variableZone
            } else 0f

            drawRoundRect(
                cornerRadius = CornerRadius(scrollbarHeight.toPx() / 2, scrollbarHeight.toPx() / 2),
                color = color,
                topLeft = Offset(scrollbarXoffset, this.size.height - scrollbarHeight.toPx()),
                size = Size(scrollbarWidth, scrollbarHeight.toPx())
            )
        }
}